import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useFormikContext } from "formik";
import { forwardRef, useEffect, useMemo, useRef } from "react";
import NumericInput from "../NumericInput";
import { formatUsd } from "../../../components/utils/formatUsdAmount";
import { truncateDecimals } from "../../../components/utils/RoundDecimals";
import clsx from "clsx";
import { useUsdTokenSync } from "../../../hooks/useUsdTokenSync";
import { ArrowUpDown } from "lucide-react";
const AmountField = forwardRef(function AmountField({ actionValue, actionValueUsd, fee, quoteTokenPrices, className, showToggle }, ref) {
    const { values, handleChange } = useFormikContext();
    const { fromAsset: fromCurrency, amount, toAsset: toCurrency, fromExchange } = values || {};
    const { setFieldValue } = useFormikContext();
    const name = "amount";
    const amountRef = useRef(ref);
    const suffixRef = useRef(null);
    const { sourceCurrencyPriceInUsd, isUsdMode, usdAmount, handleToggle, handleUsdInputChange, } = useUsdTokenSync({
        quote: quoteTokenPrices ?? fee?.quote,
        fromCurrency,
        amount,
        setFieldValue,
    });
    // --- Token mode display computations ---
    const requestedAmountInUsd = useMemo(() => {
        const amountNumber = Number(amount);
        if (isNaN(amountNumber) || amountNumber <= 0 || !sourceCurrencyPriceInUsd)
            return undefined;
        return formatUsd(sourceCurrencyPriceInUsd * amountNumber);
    }, [amount, sourceCurrencyPriceInUsd]);
    const actionValueInUsd = useMemo(() => {
        const amountNumber = Number(actionValue);
        if (isNaN(amountNumber) || amountNumber <= 0)
            return undefined;
        if (actionValueUsd)
            return formatUsd(Number(actionValueUsd));
        if (!sourceCurrencyPriceInUsd)
            return undefined;
        return formatUsd(sourceCurrencyPriceInUsd * amountNumber);
    }, [actionValue, actionValueUsd, sourceCurrencyPriceInUsd]);
    // --- USD mode display computations ---
    const actionValueAsUsd = useMemo(() => {
        if (actionValue === undefined || actionValue <= 0)
            return undefined;
        if (actionValueUsd)
            return actionValueUsd;
        if (!sourceCurrencyPriceInUsd)
            return undefined;
        return (actionValue * sourceCurrencyPriceInUsd).toFixed(2).replace(/\.?0+$/, '');
    }, [actionValue, actionValueUsd, sourceCurrencyPriceInUsd]);
    const actionValueAsToken = useMemo(() => {
        if (actionValue === undefined || actionValue <= 0)
            return undefined;
        const precision = fromCurrency?.precision || 6;
        return truncateDecimals(actionValue, precision);
    }, [actionValue, fromCurrency?.precision]);
    const formattedTokenAmount = useMemo(() => {
        const num = Number(amount);
        if (isNaN(num) || num <= 0)
            return '0';
        const precision = fromCurrency?.precision || 6;
        return truncateDecimals(num, precision);
    }, [amount, fromCurrency?.precision]);
    // --- Suffix positioning for token mode ---
    useEffect(() => {
        if (isUsdMode)
            return;
        const input = amountRef.current;
        const suffix = suffixRef.current;
        if (!input || !suffix)
            return;
        const font = getFontFromElement(input);
        const width = getTextWidth(actionValue?.toString() || amount || "0", font);
        suffix.style.left = `${width + 16}px`;
    }, [amount, requestedAmountInUsd, actionValue, isUsdMode]);
    const placeholder = '0';
    const step = 1 / Math.pow(10, fromCurrency?.precision || 1);
    const disabled = Boolean(fromExchange && !toCurrency);
    const canToggle = !!sourceCurrencyPriceInUsd;
    const toggleButton = canToggle ? (_jsx("button", { type: "button", onClick: handleToggle, className: clsx("inline-flex items-center p-0.5 rounded-md bg-secondary-300 hover:bg-secondary-200 text-secondary-text hover:text-primary-buttonTextColor transition cursor-pointer pointer-events-auto", !showToggle && "hidden group-hover/source:inline-flex"), children: _jsx(ArrowUpDown, { className: "w-3.5 h-3.5" }) })) : null;
    // --- USD mode render ---
    if (isUsdMode) {
        const previewUsd = actionValueAsUsd;
        const previewToken = actionValueAsToken;
        return (_jsxs("div", { className: clsx("flex flex-col bg-secondary-500 space-y-0.5 relative w-full", className), children: [_jsxs("div", { className: "flex items-center h-12", children: [_jsx("span", { className: "text-[28px] leading-[34px] text-primary-text font-normal mr-1 select-none", children: "$" }), _jsx("input", { type: "text", inputMode: "decimal", autoComplete: "off", autoCorrect: "off", disabled: disabled, placeholder: "0", value: previewUsd ?? usdAmount, onChange: handleUsdInputChange, className: clsx("w-full text-[28px] leading-[34px] rounded-xl focus:outline-none focus:border-none focus:ring-0 duration-300 ease-in-out font-normal px-0 truncate bg-secondary-500 border-0", previewUsd ? "text-secondary-text/45" : "text-primary-text", "placeholder:text-secondary-text") })] }), _jsxs("div", { className: "flex items-center gap-1 text-xs sm:text-base leading-5 font-medium text-secondary-text h-5 min-w-0", children: [toggleButton, _jsxs("span", { className: clsx("flex items-center min-w-0 space-x-1", { "text-secondary-text/45": !!previewToken }), children: [_jsx("span", { className: "truncate min-w-0", children: `${previewToken ?? formattedTokenAmount}` }), _jsx("span", { className: "shrink-0", children: ` ${fromCurrency?.symbol || ''}` })] })] })] }));
    }
    // --- Token mode render (default) ---
    return (_jsx(_Fragment, { children: _jsxs("div", { className: clsx("flex flex-col bg-secondary-500 space-y-0.5 relative w-full group", className), children: [_jsx(NumericInput, { disabled: disabled, placeholder: placeholder, step: isNaN(step) ? 0.01 : step, name: name, ref: amountRef, precision: fromCurrency?.precision, tempValue: actionValue, className: "w-full text-[28px] leading-[34px] rounded-xl text-primary-text focus:outline-none focus:border-none focus:ring-0 duration-300 ease-in-out bg-secondary-500! font-normal! group-[.exchange-amount-field]:text-xl group-[.exchange-amount-field]:px-2.5 group-[.exchange-amount-field]:py-2 group-[.exchange-amount-field]:pr-2 group-[.exchange-amount-field]:bg-secondary-300! px-0 truncate", onChange: e => {
                        /^[0-9]*[.,]?[0-9]*$/.test(e.target.value) && handleChange(e);
                    } }), _jsxs("div", { className: clsx("usd-suffix text-xs sm:text-base leading-5 font-medium text-secondary-text pointer-events-none h-5 flex items-center gap-1", {
                        "text-secondary-text/45": !!actionValueInUsd
                    }, "group-hover:flex"), ref: suffixRef, children: [toggleButton, _jsx("span", { children: `${actionValueInUsd ?? requestedAmountInUsd ?? '$0'}` })] })] }) }));
});
export default AmountField;
function getTextWidth(text = '', font) {
    if (typeof document === "undefined")
        return 0;
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context)
        return 0;
    context.font = font;
    return context.measureText(text).width;
}
function getFontFromElement(el) {
    if (!el)
        return '28px sans-serif';
    const style = window.getComputedStyle(el);
    return `${style.fontSize} ${style.fontFamily}`;
}
